import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import TextField from "../../../components/Form/TextField";
import Select from "../../../components/Form/Select";
import {
  FIELD_PASSWORD_INSECURE,
  FIELD_PASSWORDS_NO_MATCH,
  FIELD_REQUIRED,
  PASSWORD_CONFIRMATION_PLACEHOLDER,
  PASSWORD_PLACEHOLDER,
  REGISTER_PROMOTIONAL_CODE_LABEL,
  REGISTER_PROMOTIONAL_CODE_PLACEHOLDER,
  REGISTER_DISCOVERY_SOURCE_LABEL,
  SET_PASSWORD,
} from "../../../i18n/keysTranslations";
import { REGEX_PASSWORD } from "../../../utils/regex";
import { SIGNUP_DISCOVERY_SOURCE_OPTIONS } from "../../../utils/constants";
import { useTranslationApp } from "../../../lib/i18next";

const RegisterPasswordStep = ({ control, errors, watch }) => {
  const { t } = useTranslationApp();
  const [showPassword, setShowPassword] = useState(false);

  const discoverySourceSelectOptions = useMemo(
    () =>
      SIGNUP_DISCOVERY_SOURCE_OPTIONS.map((option) => ({
        value: option.value,
        label: t(option.labelKey),
      })),
    [t]
  );

  const onToggleShowPassword = () => setShowPassword((prev) => !prev);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        {t(SET_PASSWORD)}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <TextField
          fullWidth
          id="register-demo-password"
          name="password"
          label={t(PASSWORD_PLACEHOLDER)}
          type={showPassword ? "text" : "password"}
          variant="filled"
          color="primary"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <IconButton onClick={onToggleShowPassword}>
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
            validate: (value) => {
              if (!REGEX_PASSWORD.test(value)) {
                return t(FIELD_PASSWORD_INSECURE);
              }
            },
          }}
          errors={errors}
        />

        <TextField
          fullWidth
          id="register-demo-confirm-password"
          name="confirmPassword"
          label={t(PASSWORD_CONFIRMATION_PLACEHOLDER)}
          type={showPassword ? "text" : "password"}
          variant="filled"
          color="primary"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <IconButton onClick={onToggleShowPassword}>
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
            validate: (value) => {
              const password = watch("password");
              if (value !== password) {
                return t(FIELD_PASSWORDS_NO_MATCH);
              }
            },
          }}
          errors={errors}
        />

        <TextField
          fullWidth
          id="register-demo-promotional-code"
          label={t(REGISTER_PROMOTIONAL_CODE_LABEL)}
          name="promotionalCode"
          placeholder={t(REGISTER_PROMOTIONAL_CODE_PLACEHOLDER)}
          variant="filled"
          color="primary"
          InputLabelProps={{ shrink: true }}
          control={control}
          errors={errors}
          rules={{
            required: {
              value: false,
            },
          }}
        />

        <Select
          fullWidth
          name="discoverySource"
          control={control}
          errors={errors}
          label={t(REGISTER_DISCOVERY_SOURCE_LABEL)}
          variant="filled"
          size="medium"
          options={discoverySourceSelectOptions}
          showEmptyOption
          rules={{
            required: {
              value: false,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default RegisterPasswordStep;
